import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Link as MuiLink,
  CircularProgress
} from '@mui/material';
import { CheckBox } from '@mui/icons-material';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

const RegisterPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const registrationToken = searchParams.get('token');

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        await apiService.loadConfiguration();
        const status = await apiService.getRegistrationStatus();
        setRegistrationStatus(status);
        
        // If registration is closed and no token provided, redirect to home with message
        if (!status.registration_open && !registrationToken) {
          navigate('/', { replace: true });
          return;
        }
      } catch (error) {
        console.warn('Failed to check registration status:', error);
        // If we can't check status and no token, assume closed beta
        if (!registrationToken) {
          navigate('/', { replace: true });
          return;
        }
      } finally {
        setStatusLoading(false);
      }
    };

    checkRegistrationStatus();
  }, [registrationToken, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (registrationToken) {
        // Register with invitation link
        result = await apiService.registerWithLink(registrationToken, email, password);
        if (result.token) {
          // Set token and navigate to dashboard
          apiService.setAuthToken(result.token);
          localStorage.setItem('authToken', result.token);
          navigate('/dashboard');
          return;
        }
      } else {
        // Regular registration
        result = await register(email, password);
        if (result.success) {
          navigate('/dashboard');
          return;
        } else {
          setError(result.error);
        }
      }
    } catch (error) {
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Registration failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (statusLoading) {
    return (
      <Container component="main" maxWidth="sm">
        <Box sx={{ marginTop: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <CheckBox color="primary" sx={{ mr: 1, fontSize: 32 }} />
            <Typography component="h1" variant="h4" fontWeight={600}>
              Sign Up
            </Typography>
          </Box>

          <Typography variant="body1" color="text.secondary" textAlign="center" mb={3}>
            {registrationToken 
              ? 'Complete your registration using the invitation link'
              : 'Create your checkboxr account to get started'
            }
          </Typography>

          {registrationToken && (
            <Alert severity="success" sx={{ mb: 2 }}>
              You're registering via invitation link. Welcome to checkboxr beta!
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              helperText="Must be at least 6 characters long"
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm Password"
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
            />
            
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2, py: 1.5 }}
              disabled={isLoading || !email || !password || !confirmPassword}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create Account'
              )}
            </Button>

            <Box textAlign="center">
              <MuiLink component={Link} to="/login" variant="body2">
                Already have an account? Sign in
              </MuiLink>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default RegisterPage;